import clsx from 'clsx';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Direction } from 'types/Utilities';
import Icon from 'components/ui/Icon';
import Link from 'components/ui/Link';
import config from 'config';
import { useTranslation } from 'hooks/useTranslation';

export type Crumb = {
  path?: string;
  link?: string;
  title: string;
};

export const getBreadcrumbStructuredData = (
  crumbs: Crumb[],
  omitContext = false,
): object => {
  const { locale, t } = useTranslation();
  const { production } = config.app.domains.proxy;
  const baseUrl = `https://${production}/${locale}`;

  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Thing',
        '@id': baseUrl,
        name: t('home'),
      },
    },
  ];

  crumbs.map((crumb, i) =>
    itemListElement.push({
      '@type': 'ListItem',
      position: i + 2,
      item: {
        '@type': 'Thing',
        '@id': crumb.link ? `${baseUrl}/${crumb.link}` : crumb.title,
        name: crumb.title,
      },
    }),
  );

  let structuredData = {};
  if (omitContext) {
    structuredData = {
      '@type': 'BreadcrumbList',
      itemListElement,
    };
  } else {
    structuredData = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement,
    };
  }

  return structuredData;
};

type Props = {
  containerClassNames?: string;
  crumbs?: Crumb[];
  prefix?: string;
  theme?: 'dark' | 'light';
  useStructuredData?: boolean;
  className?: string;
};

const Breadcrumbs = ({
  containerClassNames = '',
  crumbs: _crumbs = [],
  prefix,
  theme,
  useStructuredData = true,
  className,
}: Props): JSX.Element => {
  const { locale, t } = useTranslation();
  const { route } = useRouter();

  let crumbs = _crumbs;
  if (prefix) {
    crumbs = [
      { path: prefix, link: prefix, title: t(`${prefix}`) },
      ..._crumbs,
    ];
  }

  let structuredData = null;
  if (useStructuredData) {
    structuredData = getBreadcrumbStructuredData(crumbs);
  }

  return (
    <>
      {useStructuredData && (
        <Head>
          <script
            type="application/ld+json"
            key="breadcrumbsStructuredData"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        </Head>
      )}
      <div
        className={clsx(
          'max-w-screen-xl mx-auto px-5 md:px-4',
          containerClassNames,
        )}
      >
        <ul
          className={clsx(
            'breadcrumb flex items-center text-sm py-13p md:py-6',
            theme === 'light' ? 'text-white' : 'text-charcoal',
            className,
          )}
        >
          <li>
            <Link href={`/${locale}`}>
              <a
                title={t('home')}
                className={clsx(
                  route !== '/[locale]' && theme === 'light'
                    ? 'text-white opacity-50'
                    : 'text-primary',
                )}
              >
                <Icon type="House" />
              </a>
            </Link>
          </li>
          {crumbs !== null &&
            crumbs.map((crumb, i) => {
              return (
                <li
                  key={i}
                  className={clsx(
                    'whitespace-nowrap',
                    i === crumbs.length - 1 && 'truncate',
                  )}
                >
                  <Icon
                    className={clsx(
                      'mx-3 w-1 inline-block',
                      theme === 'light'
                        ? 'opacity-50 text-white'
                        : 'text-primary',
                    )}
                    type="Angle"
                    direction={Direction.RIGHT}
                  />
                  {i < crumbs.length - 1 && crumb.link ? (
                    <Link href={`/${locale}/${crumb.link}`}>
                      <a className="text-primary" title={crumb.title}>
                        <span
                          className={clsx(
                            crumbs.length > 2 &&
                              i < crumbs.length - 1 &&
                              'hidden sm:inline-block',
                            theme === 'light' && 'opacity-50 text-white',
                          )}
                        >
                          {crumb.title}
                        </span>
                        <span
                          className={clsx(
                            crumbs.length > 2 && i <= crumbs.length - 2
                              ? 'sm:hidden'
                              : 'hidden',
                            theme === 'light' && 'opacity-50',
                          )}
                        >
                          ...
                        </span>
                      </a>
                    </Link>
                  ) : (
                    <span className="font-bold">{crumb.title}</span>
                  )}
                </li>
              );
            })}
        </ul>
      </div>
    </>
  );
};

export default Breadcrumbs;
